import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Gift, Star, Users, MessageCircle } from "lucide-react";

const REWARDS = [
  {
    icon: Star,
    points: "15 Points",
    reward: "Free Wellness Shot",
    color: "#A3B18A",
  },
  {
    icon: Gift,
    points: "25 Points",
    reward: "Any Cold Press Juice",
    color: "#F0B7A4",
  },
  {
    icon: Users,
    points: "35 Points",
    reward: "Family Pack",
    color: "#A3B18A",
  },
];

const FAQ_ITEMS = [
  {
    q: "What are Zumo Points?",
    a: "Zumo Points are reward points you earn every time you order from us. You can redeem them for FREE drinks and offers.",
  },
  {
    q: "How do I earn Zumo Points?",
    a: "You earn 1 point for every QAR 10 spent on your orders — including orders via Talabat & Snoonu.",
  },
  {
    q: "How do I redeem my points?",
    a: "Just message us on WhatsApp and we'll help you redeem your points on your next order.",
  },
  {
    q: "Do my points expire?",
    a: "Yes, Zumo Points are valid for 1 year from the date of earning.",
  },
  {
    q: "Can I earn points on delivery app orders?",
    a: "Yes! You earn points even when you order via Talabat & Snoonu.",
  },
  {
    q: "How can I check my points balance?",
    a: "Simply WhatsApp us and we'll share your current balance.",
  },
  {
    q: "Any terms I should know?",
    a: "Points are non-transferable and cannot be exchanged for cash. T&C apply.",
  },
];

export default function ZumoPoints() {
  return (
    <main data-testid="zumo-points-page" style={{ background: "#FCF8F6" }}>
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#A3B18A",
              }}
            >
              Loyalty Rewards
            </p>
            <h1
              className="text-5xl sm:text-6xl tracking-tighter leading-none font-medium mb-6"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "#283618",
              }}
            >
              Zumo Points
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#6F5E58",
              }}
            >
              Every sip rewards you. Earn points on every order and redeem
              them for free drinks, wellness shots, and family packs.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        className="py-20 md:py-28"
        style={{ background: "#F5D5CB" }}
        data-testid="how-it-works"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{
              fontFamily: "'Manrope', sans-serif",
              color: "#6F5E58",
            }}
          >
            How It Works
          </p>
          <h2
            className="text-4xl md:text-5xl tracking-tight leading-tight font-medium mb-6"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "#283618",
            }}
          >
            Earn 1 point for every QAR 10 spent
          </h2>
          <p
            className="text-sm md:text-base leading-relaxed max-w-lg mx-auto mb-16"
            style={{
              fontFamily: "'Manrope', sans-serif",
              color: "#6F5E58",
            }}
          >
            Order through Talabat or Snoonu and earn points on every purchase. Redeem via WhatsApp anytime.
          </p>

          {/* Rewards Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {REWARDS.map((tier, i) => (
              <div
                key={tier.points}
                data-testid={`reward-tier-${i}`}
                className="rounded-3xl p-8 text-center transition-transform duration-300 hover:-translate-y-1"
                style={{ background: "#FCF8F6" }}
              >
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
                  style={{ background: `${tier.color}30` }}
                >
                  <tier.icon size={26} color="#283618" />
                </div>
                <span
                  className="text-3xl font-medium block mb-2"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: "#283618",
                  }}
                >
                  {tier.points}
                </span>
                <p
                  className="text-sm font-semibold uppercase tracking-wider"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                  }}
                >
                  {tier.reward}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-24 md:py-32"
        data-testid="zumo-points-faq"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#A3B18A",
              }}
            >
              FAQ
            </p>
            <h2
              className="text-4xl md:text-5xl tracking-tight leading-tight font-medium"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "#283618",
              }}
            >
              Common Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="rounded-2xl border-none px-6"
                style={{ background: "#F5D5CB" }}
              >
                <AccordionTrigger
                  className="text-left py-5 hover:no-underline"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#2A2A2A",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="pb-5"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    color: "#6F5E58",
                    fontSize: "0.875rem",
                    lineHeight: "1.7",
                  }}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 md:py-24 text-center"
        style={{ background: "#283618" }}
        data-testid="zumo-points-cta"
      >
        <div className="max-w-2xl mx-auto px-6">
          <h2
            className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-4"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "#FCF8F6",
            }}
          >
            Ready to start earning?
          </h2>
          <p
            className="text-sm md:text-base leading-relaxed mb-8"
            style={{
              fontFamily: "'Manrope', sans-serif",
              color: "rgba(252, 248, 246, 0.6)",
            }}
          >
            Check your balance or redeem your points — just send us a
            message on WhatsApp.
          </p>
          <a
            href="https://wa.me/97466846261?text=Hi%2C%20I'd%20like%20to%20check%20my%20Zumo%20Points%20balance"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              data-testid="points-whatsapp-btn"
              className="rounded-full px-8 py-3 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 mx-auto transition-all duration-300 hover:scale-105"
              style={{
                background: "#F0B7A4",
                color: "#283618",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <MessageCircle size={16} />
              WhatsApp Us
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
